import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Copy, Send, Trash2 } from "lucide-react";
import { addDays, format, isSameDay, parseISO, subDays } from "date-fns";
import { PageShell, PageHeader } from "@/components/layout/PageShell";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/Badge";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SearchableCombobox } from "@/components/ui/SearchableCombobox";
import { DurationInput } from "@/components/ui/DurationInput";
import { useAuth } from "@/auth/AuthProvider";
import { getDataAdapter } from "@/data";
import { emitDataEvent } from "@/data/events";
import { weekDays, weekStart, formatWeekRange } from "@/utils/date";
import { durationMinutes, durationUnit, durationRule, entryHours, formatDuration, formatHours, roundDurationMinutes } from "@/utils/duration";
// Per-cell debounce window for auto-save. Long enough to coalesce a normal
// "type a number and tab away" sequence into one round-trip; short enough that
// the row state matches Dataverse before the user moves on.
const SAVE_DEBOUNCE_MS = 700;
export function WeeklyTimesheet() {
    const adapter = getDataAdapter();
    const { user } = useAuth();
    const [cursor, setCursor] = useState(new Date());
    const [rows, setRows] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [projects, setProjects] = useState([]);
    const [transCats, setTransCats] = useState([]);
    const [reloadKey, setReloadKey] = useState(0);
    const [activeDay, setActiveDay] = useState(0);
    const [pendingSaves, setPendingSaves] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [savingCells, setSavingCells] = useState(new Set());
    // Refs let the debounced auto-save callback read the latest rows/days
    // without having to re-create the timer on every keystroke.
    const rowsRef = useRef([]);
    const timersRef = useRef(new Map());
    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);
    const wStart = useMemo(() => weekStart(cursor), [cursor]);
    const days = useMemo(() => weekDays(cursor), [cursor]);
    const daysRef = useRef(days);
    useEffect(() => {
        daysRef.current = days;
    }, [days]);
    useEffect(() => {
        void Promise.all([
            adapter.listAccounts(true),
            adapter.listWorkOrders(),
            adapter.listProjects(),
            adapter.listTransactionCategories(),
        ]).then(([a, w, p, tc]) => {
            setAccounts(a);
            setWorkOrders(w);
            setProjects(p);
            setTransCats(tc);
            setCategoriesLoaded(true);
        }).catch((err) => {
            console.error("Timesheet lookups failed", err);
            setSaveError(err instanceof Error ? err.message : String(err));
        });
    }, [adapter]);
    useEffect(() => {
        void adapter
            .listTimeEntries({
            resourceId: user.bookableResourceId,
            from: wStart.toISOString(),
            to: addDays(wStart, 7).toISOString(),
        })
            .then((list) => {
            const map = new Map();
            for (const e of list) {
                if (!e.regardingobjectid)
                    continue;
                const key = `${e.regardingobjectid.type}:${e.regardingobjectid.id}:${e.msdyn_transactioncategoryid ?? ""}`;
                if (!map.has(key)) {
                    map.set(key, {
                        key,
                        regarding: e.regardingobjectid,
                        transactionCategoryId: e.msdyn_transactioncategoryid,
                        hoursByDay: Array(7).fill(0),
                        entriesByDay: Array.from({ length: 7 }, () => []),
                        statuses: [],
                    });
                }
                const row = map.get(key);
                const dayIdx = days.findIndex((d) => isSameDay(d, parseISO(e.msdyn_start)));
                if (dayIdx >= 0) {
                    row.hoursByDay[dayIdx] += entryHours(e);
                    row.entriesByDay[dayIdx].push(e);
                }
                row.statuses.push(e.msdyn_entryStatus);
            }
            setRows(Array.from(map.values()));
        });
    }, [adapter, user.bookableResourceId, wStart, days, reloadKey]);
    // Clear any pending save timers on unmount so they can't fire against a
    // stale component.
    useEffect(() => {
        return () => {
            for (const t of timersRef.current.values())
                window.clearTimeout(t);
            timersRef.current.clear();
        };
    }, []);
    const totalsByDay = useMemo(() => {
        const t = Array(7).fill(0);
        for (const r of rows)
            r.hoursByDay.forEach((h, i) => (t[i] += h));
        return t;
    }, [rows]);
    const weekTotal = totalsByDay.reduce((a, b) => a + b, 0);
    const regardingOptions = useMemo(() => [
        ...accounts.map((a) => ({ value: `account:${a.accountid}`, label: a.name, hint: "Account" })),
        ...workOrders.map((w) => ({
            value: `msdyn_workorder:${w.msdyn_workorderid}`,
            label: w.msdyn_name,
            hint: "Work Order",
        })),
        ...projects.map((p) => ({
            value: `msdyn_project:${p.msdyn_projectid}`,
            label: p.msdyn_subject,
            hint: "Project",
        })),
    ], [accounts, workOrders, projects]);
    const transCatOptions = useMemo(() => transCats.map((t) => ({
        value: t.msdyn_transactioncategoryid,
        label: t.msdyn_description,
        hint: t.msdyn_billingtype,
    })), [transCats]);
    const scheduleSave = (rowKey, dayIdx) => {
        const cellKey = `${rowKey}:${dayIdx}`;
        const existing = timersRef.current.get(cellKey);
        if (existing !== undefined) {
            window.clearTimeout(existing);
        }
        else {
            setPendingSaves((n) => n + 1);
        }
        const t = window.setTimeout(() => {
            timersRef.current.delete(cellKey);
            setSavingCells((prev) => new Set(prev).add(cellKey));
            void saveCellNow(rowKey, dayIdx).catch((err) => {
                console.error("Auto-save failed", err);
                setSaveError(err instanceof Error ? err.message : String(err));
                setReloadKey((k) => k + 1);
            }).finally(() => {
                setSavingCells((prev) => {
                    const next = new Set(prev);
                    next.delete(cellKey);
                    return next;
                });
                setPendingSaves((n) => Math.max(0, n - 1));
            });
        }, SAVE_DEBOUNCE_MS);
        timersRef.current.set(cellKey, t);
    };
    const saveCellNow = async (rowKey, dayIdx) => {
        const row = rowsRef.current.find((r) => r.key === rowKey);
        if (!row)
            return;
        const targetHours = row.hoursByDay[dayIdx];
        const existing = row.entriesByDay[dayIdx];
        if (existing.some((e) => e.msdyn_entryStatus === "Approved"))
            return;
        if (!Number.isFinite(targetHours) || targetHours < 0) {
            throw new Error("Duration must be a finite, non-negative number.");
        }
        // Clearing a cell removes its writable entries; approved cells are locked.
        if (targetHours <= 0) {
            if (existing.length === 0)
                return;
            for (const e of existing)
                await adapter.deleteTimeEntry(e.msdyn_timeentryid);
            setRows((prev) => prev.map((r) => {
                if (r.key !== rowKey)
                    return r;
                const entriesByDay = r.entriesByDay.map((d, i) => (i === dayIdx ? [] : d));
                const hoursByDay = [...r.hoursByDay];
                hoursByDay[dayIdx] = 0;
                const statuses = entriesByDay.flat().map((e) => e.msdyn_entryStatus);
                return { ...r, entriesByDay, hoursByDay, statuses };
            }));
            emitDataEvent("entries-changed");
            return;
        }
        // Hours > 0 and no backing entry yet → create one. 09:00 local is the
        // default start; users tweak exact times in the entry dialog if needed.
        const day = daysRef.current[dayIdx];
        const start = new Date(day);
        start.setHours(9, 0, 0, 0);
        const minutes = roundDurationMinutes(targetHours * 60, durationUnit(row.transactionCategoryId, transCats));
        if (existing.length === 0) {
            const end = new Date(start.getTime() + minutes * 60000);
            const created = await adapter.createTimeEntry({
                msdyn_description: row.regarding.display,
                msdyn_start: start.toISOString(),
                msdyn_end: end.toISOString(),
                msdyn_duration: minutes,
                msdyn_type: "Work",
                msdyn_entryStatus: "Draft",
                bookableresource: user.bookableResourceId,
                regardingobjectid: row.regarding,
                msdyn_transactioncategoryid: row.transactionCategoryId,
            });
            setRows((prev) => prev.map((r) => {
                if (r.key !== rowKey)
                    return r;
                const entriesByDay = r.entriesByDay.map((d, i) => i === dayIdx ? [...d, created] : d);
                const statuses = entriesByDay.flat().map((e) => e.msdyn_entryStatus);
                const hoursByDay = [...r.hoursByDay];
                hoursByDay[dayIdx] = entryHours(created);
                return { ...r, entriesByDay, hoursByDay, statuses };
            }));
            emitDataEvent("entries-changed");
            return;
        }
        const [first, ...rest] = existing;
        const firstStart = parseISO(first.msdyn_start);
        const newEnd = new Date(firstStart.getTime() + minutes * 60000);
        const updated = await adapter.updateTimeEntry(first.msdyn_timeentryid, {
            msdyn_end: newEnd.toISOString(),
            msdyn_duration: minutes,
            msdyn_transactioncategoryid: row.transactionCategoryId,
        });
        for (const e of rest)
            await adapter.deleteTimeEntry(e.msdyn_timeentryid);
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey)
                return r;
            const entriesByDay = r.entriesByDay.map((d, i) => (i === dayIdx ? [updated] : d));
            const hoursByDay = [...r.hoursByDay];
            hoursByDay[dayIdx] = entryHours(updated);
            const statuses = entriesByDay.flat().map((e) => e.msdyn_entryStatus);
            return { ...r, entriesByDay, hoursByDay, statuses };
        }));
        emitDataEvent("entries-changed");
    };
    const setCellHours = (rowKey, dayIdx, hours) => {
        setSaveError(null);
        setRows((prev) => prev.map((r) => {
            if (r.key !== rowKey)
                return r;
            const hoursByDay = [...r.hoursByDay];
            hoursByDay[dayIdx] = hours;
            return { ...r, hoursByDay };
        }));
        scheduleSave(rowKey, dayIdx);
    };
    const setRowCategory = async (rowKey, categoryId) => {
        const row = rowsRef.current.find((r) => r.key === rowKey);
        if (!row)
            return;
        const newCatId = categoryId ?? undefined;
        setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, transactionCategoryId: newCatId } : r)));
        const writable = row.entriesByDay.flat().filter((e) => e.msdyn_entryStatus !== "Approved");
        if (writable.length === 0)
            return;
        setPendingSaves((n) => n + 1);
        try {
            const updates = await Promise.all(writable.map((e) => adapter.updateTimeEntry(e.msdyn_timeentryid, {
                msdyn_transactioncategoryid: newCatId,
            })));
            const byId = new Map(updates.map((u) => [u.msdyn_timeentryid, u]));
            setRows((prev) => prev.map((r) => {
                if (r.key !== rowKey)
                    return r;
                const entriesByDay = r.entriesByDay.map((d) => d.map((e) => byId.get(e.msdyn_timeentryid) ?? e));
                return { ...r, entriesByDay };
            }));
            emitDataEvent("entries-changed");
        }
        catch (err) {
            console.error("Category save failed", err);
            setSaveError(err instanceof Error ? err.message : String(err));
            setReloadKey((k) => k + 1);
        }
        finally {
            setPendingSaves((n) => Math.max(0, n - 1));
        }
    };
    const addRow = (val) => {
        if (!val)
            return;
        const [type, id] = val.split(":");
        const item = regardingOptions.find((o) => o.value === val);
        if (!item)
            return;
        const regarding = {
            type: type,
            id,
            display: item.label,
        };
        // Synthetic key — lets the same regarding appear on multiple rows (e.g.
        // two service types against the same account) without React-key clashes.
        // Server-grouped rows still use the deterministic "type:id:catId" form.
        const key = `new:${Date.now()}:${Math.random().toString(36).slice(2, 8)}:${type}:${id}`;
        setRows((prev) => [
            ...prev,
            {
                key,
                regarding,
                hoursByDay: Array(7).fill(0),
                entriesByDay: Array.from({ length: 7 }, () => []),
                statuses: [],
            },
        ]);
    };
    const [submitError, setSubmitError] = useState(null);
    const submitWeek = async () => {
        const ids = rowsRef.current
            .flatMap((r) => r.entriesByDay.flat())
            .filter((e) => e.msdyn_entryStatus === "Draft")
            .map((e) => e.msdyn_timeentryid);
        if (ids.length === 0)
            return;
        setSubmitError(null);
        try {
            await adapter.submitEntries(ids);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error("Submit week failed:", err);
            setSubmitError(msg);
        }
        finally {
            setReloadKey((k) => k + 1);
        }
    };
    const duplicateLastWeek = async () => {
        const lastStart = subDays(wStart, 7);
        const last = await adapter.listTimeEntries({
            resourceId: user.bookableResourceId,
            from: lastStart.toISOString(),
            to: addDays(lastStart, 7).toISOString(),
        });
        for (const e of last) {
            const diff = parseISO(e.msdyn_start).getTime() - lastStart.getTime();
            const newStart = new Date(wStart.getTime() + diff);
            const dur = durationMinutes(e) * 60000;
            const { msdyn_timeentryid: _id, ...rest } = e;
            void _id;
            await adapter.createTimeEntry({
                ...rest,
                msdyn_start: newStart.toISOString(),
                msdyn_end: new Date(newStart.getTime() + dur).toISOString(),
                msdyn_entryStatus: "Draft",
            });
        }
        setReloadKey((k) => k + 1);
    };
    const requestRemoveRow = (rowKey, label) => {
        setConfirmDelete({ rowKey, label });
    };
    const performRemoveRow = async () => {
        if (!confirmDelete)
            return;
        const row = rowsRef.current.find((r) => r.key === confirmDelete.rowKey);
        if (!row) {
            setConfirmDelete(null);
            return;
        }
        setDeleting(true);
        try {
            const all = row.entriesByDay.flat();
            let deleted = 0;
            for (const e of all) {
                if (e.msdyn_entryStatus === "Approved")
                    continue;
                try {
                    await adapter.deleteTimeEntry(e.msdyn_timeentryid);
                    deleted++;
                }
                catch (err) {
                    console.error("Row delete failed", err);
                }
            }
            setRows((prev) => prev.filter((r) => r.key !== confirmDelete.rowKey));
            if (deleted > 0)
                emitDataEvent("entries-changed");
        }
        finally {
            setDeleting(false);
            setConfirmDelete(null);
        }
    };
    const dominantStatus = (statuses) => {
        if (statuses.length === 0)
            return "Draft";
        if (statuses.every((s) => s === "Approved"))
            return "Approved";
        if (statuses.some((s) => s === "Returned"))
            return "Returned";
        if (statuses.some((s) => s === "Submitted"))
            return "Submitted";
        return "Draft";
    };
    const isRowApproved = (r) => r.statuses.length > 0 && r.statuses.every((s) => s === "Approved");
    return (_jsxs(PageShell, { children: [_jsx(PageHeader, { icon: _jsx(CalendarDays, { size: 20 }), title: "Weekly Timesheet", subtitle: "Excel-style grid grouped by regarding record. Edits save automatically.", right: _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs(Button, { variant: "ghost", onClick: duplicateLastWeek, disabled: pendingSaves > 0, leftIcon: _jsx(Copy, { size: 14 }), children: [_jsx("span", { className: "hidden sm:inline", children: "Duplicate last week" }), _jsx("span", { className: "sm:hidden", children: "Duplicate" })] }), _jsxs(Button, { onClick: submitWeek, disabled: pendingSaves > 0, leftIcon: _jsx(Send, { size: 14 }), children: [_jsx("span", { className: "hidden sm:inline", children: "Submit week" }), _jsx("span", { className: "sm:hidden", children: "Submit" })] })] }) }), saveError && (_jsxs("div", { role: "alert", className: "rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700", children: ["Could not save timesheet: ", saveError] })), submitError && (_jsx(Card, { className: "border-rose-200 bg-rose-50/70 p-4", children: _jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("div", { className: "text-sm font-semibold text-rose-900", children: "Submit week reported errors" }), _jsx("pre", { className: "mt-1 whitespace-pre-wrap break-words text-xs text-rose-800", children: submitError })] }), _jsx("button", { type: "button", onClick: () => setSubmitError(null), className: "rounded-md px-2 py-1 text-xs text-rose-700 hover:bg-rose-100", children: "Dismiss" })] }) })), _jsxs(Card, { className: "p-0 overflow-hidden", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-slate-200 px-4 sm:px-5 py-3 gap-2", children: [_jsxs("div", { className: "flex items-center gap-2 min-w-0", children: [_jsx(Button, { variant: "subtle", disabled: pendingSaves > 0, onClick: () => setCursor(subDays(cursor, 7)), children: _jsx(ChevronLeft, { size: 14 }) }), _jsx("div", { className: "text-sm font-medium text-slate-700 truncate", children: formatWeekRange(cursor) }), _jsx(Button, { variant: "subtle", disabled: pendingSaves > 0, onClick: () => setCursor(addDays(cursor, 7)), children: _jsx(ChevronRight, { size: 14 }) })] }), _jsxs("div", { className: "flex items-center gap-3 text-sm text-slate-500 whitespace-nowrap", children: [pendingSaves > 0 && (_jsxs("span", { className: "hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-500", children: [_jsx("span", { className: "h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" }), "Saving\u2026"] })), _jsxs("span", { children: [_jsx("span", { className: "hidden sm:inline", children: "Week total: " }), _jsxs("span", { className: "font-semibold text-slate-900", children: [formatHours(weekTotal), "h"] })] })] })] }), _jsxs("div", { className: "md:hidden", children: [_jsx("div", { className: "flex gap-1.5 overflow-x-auto border-b border-slate-100 px-3 py-2", children: days.map((d, i) => {
                                    const dayTotal = rows.reduce((s, r) => s + r.hoursByDay[i], 0);
                                    const active = i === activeDay;
                                    return (_jsxs("button", { onClick: () => setActiveDay(i), className: "flex flex-col items-center min-w-[58px] rounded-lg px-2 py-1.5 text-xs " +
                                            (active
                                                ? "bg-brand-500 text-white"
                                                : "bg-slate-50 text-slate-600 hover:bg-slate-100"), children: [_jsx("span", { className: "font-medium", children: format(d, "EEE") }), _jsx("span", { className: "text-[10px] " + (active ? "text-white/80" : "text-slate-400"), children: format(d, "d MMM") }), _jsx("span", { className: "mt-0.5 tabular-nums " + (active ? "text-white" : "text-slate-700 font-semibold"), children: dayTotal > 0 ? `${formatHours(dayTotal)}h` : "—" })] }, d.toISOString()));
                                }) }), _jsxs("ul", { className: "divide-y divide-slate-100", children: [rows.map((r) => {
                                        const st = dominantStatus(r.statuses);
                                        const approved = isRowApproved(r);
                                        const unit = durationUnit(r.transactionCategoryId, transCats);
                                        const cellLocked = r.entriesByDay[activeDay].some((e) => e.msdyn_entryStatus === "Approved");
                                        return (_jsxs("li", { className: "p-3 space-y-2", children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("div", { className: "font-medium text-slate-900 truncate", children: r.regarding.display }), _jsx("div", { className: "text-[10px] uppercase tracking-wide text-slate-500", children: r.regarding.type === "account"
                                                                        ? "Account"
                                                                        : r.regarding.type === "msdyn_workorder"
                                                                            ? "Work Order"
                                                                            : "Project" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(StatusBadge, { status: st }), _jsx("button", { type: "button", onClick: () => requestRemoveRow(r.key, r.regarding.display), disabled: approved, title: approved ? "Approved rows can't be removed" : "Remove row", className: "rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400", children: _jsx(Trash2, { size: 14 }) })] })] }), _jsx(SearchableCombobox, { items: transCatOptions, value: r.transactionCategoryId ?? null, onChange: (v) => void setRowCategory(r.key, v), placeholder: "Pick a service type", disabled: approved || !categoriesLoaded || pendingSaves > 0 }), _jsxs("div", { className: "flex items-center justify-between gap-3", children: [_jsxs("span", { className: "text-xs text-slate-500", children: [unit === "Minute" ? "Minutes" : "Hours", " for ", format(days[activeDay], "EEE d MMM")] }), _jsx(DurationInput, { unit: unit, minutes: r.hoursByDay[activeDay] * 60, allowClear: true, disabled: cellLocked || !categoriesLoaded || savingCells.has(`${r.key}:${activeDay}`), onChange: (minutes) => setCellHours(r.key, activeDay, minutes / 60), className: "w-24 rounded-md border border-slate-200 px-3 py-2 text-center text-sm tabular-nums focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none" })] }), _jsxs("p", { className: "text-xs text-slate-500", children: [durationRule(unit), " Clear to remove."] })] }, r.key));
                                    }), rows.length === 0 && (_jsx("li", { className: "px-4 py-8 text-center text-slate-400 text-sm", children: "No rows yet. Add an account, work order, or project below." }))] }), _jsxs("div", { className: "flex items-center justify-between border-t border-slate-100 bg-slate-50/80 px-4 py-3 text-sm font-medium text-slate-900", children: [_jsxs("span", { className: "text-xs uppercase tracking-wide text-slate-500", children: [format(days[activeDay], "EEE"), " total"] }), _jsxs("span", { className: "tabular-nums", children: [formatHours(totalsByDay[activeDay]), "h"] })] })] }), _jsx("div", { className: "hidden md:block overflow-x-auto", children: _jsxs("table", { className: "w-full min-w-[960px] text-sm", children: [_jsx("thead", { className: "bg-slate-50 text-xs uppercase tracking-wide text-slate-500", children: _jsxs("tr", { children: [_jsx("th", { className: "text-left px-4 py-2.5 font-medium w-[26%]", children: "Regarding" }), _jsx("th", { className: "text-left px-2 py-2.5 font-medium w-[14%]", children: "Service type" }), days.map((d) => (_jsxs("th", { className: "px-2 py-2.5 text-center font-medium", children: [_jsx("div", { children: format(d, "EEE") }), _jsx("div", { className: "text-[10px] text-slate-400", children: format(d, "d MMM") })] }, d.toISOString()))), _jsx("th", { className: "px-2 py-2.5 text-right font-medium", children: "Total" }), _jsx("th", { className: "px-2 py-2.5 text-right font-medium", children: "Status" }), _jsx("th", { className: "px-2 py-2.5 text-right font-medium w-[40px]" })] }) }), _jsxs("tbody", { className: "divide-y divide-slate-100", children: [rows.map((r) => {
                                            const total = r.hoursByDay.reduce((a, b) => a + b, 0);
                                            const st = dominantStatus(r.statuses);
                                            const approved = isRowApproved(r);
                                            const unit = durationUnit(r.transactionCategoryId, transCats);
                                            return (_jsxs("tr", { className: "hover:bg-slate-50/50", children: [_jsxs("td", { className: "px-4 py-2.5", children: [_jsx("div", { className: "font-medium text-slate-900 truncate", children: r.regarding.display }), _jsx("div", { className: "text-xs text-slate-500 uppercase tracking-wide", children: r.regarding.type === "account"
                                                                    ? "Account"
                                                                    : r.regarding.type === "msdyn_workorder"
                                                                        ? "Work Order"
                                                                        : "Project" })] }), _jsxs("td", { className: "px-2 py-2.5", children: [_jsx(SearchableCombobox, { items: transCatOptions, value: r.transactionCategoryId ?? null, onChange: (v) => void setRowCategory(r.key, v), placeholder: "\u2014", disabled: approved || !categoriesLoaded || pendingSaves > 0 }), _jsx("div", { className: "mt-1 text-xs text-slate-500", title: durationRule(unit), children: unit === "Minute" ? "Minutes (min. 15)" : "Hours (min. 1)" })] }), r.hoursByDay.map((h, i) => (_jsx("td", { className: "px-1 py-1.5 text-center", children: _jsx(DurationInput, { unit: unit, minutes: h * 60, allowClear: true, disabled: !categoriesLoaded || savingCells.has(`${r.key}:${i}`) || r.entriesByDay[i].some((e) => e.msdyn_entryStatus === "Approved"), onChange: (minutes) => setCellHours(r.key, i, minutes / 60), className: "w-16 rounded-md border border-slate-200 px-2 py-1.5 text-center text-sm tabular-nums focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none" }) }, i))), _jsx("td", { className: "px-2 py-2.5 text-right tabular-nums font-medium text-slate-900", children: formatDuration(Math.round(total * 60), unit) }), _jsx("td", { className: "px-2 py-2.5 text-right", children: _jsx(StatusBadge, { status: st }) }), _jsx("td", { className: "px-2 py-2.5 text-right", children: _jsx("button", { type: "button", onClick: () => requestRemoveRow(r.key, r.regarding.display), disabled: approved, title: approved ? "Approved rows can't be removed" : "Remove row", className: "rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400", children: _jsx(Trash2, { size: 14 }) }) })] }, r.key));
                                        }), rows.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: 12, className: "px-4 py-8 text-center text-slate-400", children: "No rows yet. Add an account, work order, or project below." }) }))] }), _jsx("tfoot", { children: _jsxs("tr", { className: "bg-slate-50/80 font-medium text-slate-900", children: [_jsx("td", { colSpan: 2, className: "px-4 py-2.5 text-right text-xs uppercase tracking-wide text-slate-500", children: "Totals (hours)" }), totalsByDay.map((t, i) => (_jsx("td", { className: "px-2 py-2.5 text-center tabular-nums", children: t > 0 ? formatHours(t) : "—" }, i))), _jsx("td", { className: "px-2 py-2.5 text-right tabular-nums", children: formatHours(weekTotal) }), _jsx("td", {}), _jsx("td", {})] }) })] }) }), _jsx("div", { className: "border-t border-slate-100 bg-slate-50/60 px-4 py-3", children: _jsx("div", { className: "max-w-md", children: _jsx(SearchableCombobox, { label: "Add row", items: regardingOptions, value: null, onChange: addRow, placeholder: "Search accounts, work orders, projects\u2026" }) }) })] }), _jsx(ConfirmDialog, { open: confirmDelete !== null, onClose: () => setConfirmDelete(null), onConfirm: performRemoveRow, title: "Remove row?", body: _jsxs(_Fragment, { children: ["This will permanently delete all draft and submitted time entries on this row for", " ", _jsx("span", { className: "font-medium text-slate-900", children: confirmDelete?.label }), " in the current week. Approved entries are left untouched."] }), confirmLabel: "Remove row", confirmVariant: "danger", loading: deleting })] }));
}
